<script setup lang="ts">
    import { __ } from '@/js/stores/expressions';
    import { useScheduleCoaching } from '@/js/stores/scheduleCoaching';

    import TextareaInput from '../../TextareaInput.vue';
    import TextInput, { TextInputWidth } from '../../TextInput.vue';
    import RadioOption from '../../RadioOption.vue';
    import SelectInput from '../../SelectInput.vue';
    import ScheduleCoachingIntervalForm from './ScheduleCoachingIntervalForm.vue';
    import { useGetCoachingForms } from '@/js/services/api/coachingForm/coachingFormQueries';
    import { computed } from 'vue';
    import { SCHEDULE_COACHING_VISIBILITY_MODE } from '@/js/services/api/scheduleCoachingForm/types';
    import InputLabel from '../../InputLabel.vue';
    import FormGroup from '../../FormGroup.vue';

    const scheduleCoachingStore = useScheduleCoaching();

    const { data: forms, isLoading: isLoadingForms } = useGetCoachingForms();

    const aiForms = computed(() => {
        if (!forms.value) return [];

        return forms.value.forms
            .filter((form) => form.isActive && form.useAi)
            .sort((a, b) => a.title.localeCompare(b.title));
    });
</script>
<template>
    <FormGroup>
        <div>
            <h3 class="heading-md">
                {{ __('schedule-coaching-form-title') }}
            </h3>
            <p class="text-body mb-4">
                {{ __('schedule-coaching-form-description') }}
            </p>
        </div>

        <div>
            <InputLabel for="schedule-coaching-form-title-input">
                {{ __('schedule-coaching-form-title-input') }}
            </InputLabel>
            <TextInput
                id="schedule-coaching-form-title-input"
                v-model="scheduleCoachingStore.formData.title"
                :placeholder="__('schedule-coaching-form-title-input-placeholder')"
                :width="TextInputWidth.Full"
            />
        </div>

        <div>
            <InputLabel for="schedule-coaching-form-description-title">
                {{ __('schedule-coaching-form-description-title') }}
            </InputLabel>
            <TextareaInput
                id="schedule-coaching-form-description-title"
                v-model="scheduleCoachingStore.formData.description"
                :placeholder="__('schedule-coaching-form-description-placeholder')"
                :rows="3"
            />
        </div>

        <div>
            <InputLabel for="schedule-coaching-form-select-form-title">
                {{ __('schedule-coaching-form-select-form-title') }}
            </InputLabel>
            <SelectInput
                id="schedule-coaching-form-select-form-title"
                v-model="scheduleCoachingStore.formData.formId"
                :disabled="isLoadingForms"
                size="full"
            >
                <optgroup v-if="aiForms.length > 0">
                    <option v-for="form in aiForms" :key="form.id" :value="form.id">
                        {{ form.title }}
                    </option>
                </optgroup>
            </SelectInput>
        </div>

        <FormGroup :help-hint="__('show-example')">
            <template #header>
                {{ __('schedule-coaching-form-visibility-title') }}
            </template>
            <template #help>
                <div class="whitespace-pre-line">
                    {{ __('schedule-coaching-form-visibility-help') }}
                </div>
            </template>
            <div class="flex gap-4 flex-wrap mt-1.5">
                <RadioOption
                    id="OwnerOnly"
                    v-model="scheduleCoachingStore.formData.visibilityMode"
                    name="visibilityMode"
                    :label="__('schedule-coaching-form-visibility-owner-only')"
                    :value="SCHEDULE_COACHING_VISIBILITY_MODE.OWNER_ONLY"
                    :disabled="true"
                />
                <RadioOption
                    id="remote"
                    v-model="scheduleCoachingStore.formData.visibilityMode"
                    name="visibilityMode"
                    :label="__('schedule-coaching-form-visibility-all-authorized-users')"
                    :value="SCHEDULE_COACHING_VISIBILITY_MODE.ALL_AUTHORIZED_USERS"
                    :disabled="true"
                />
            </div>
        </FormGroup>

        <div>
            <InputLabel
                for="schedule-coaching-form-interactions-per-run-title"
                :help-hint="__('show-info')"
            >
                {{ __('schedule-coaching-form-interactions-per-run-title') }}

                <template #help>
                    {{ __('schedule-coaching-form-interactions-per-run-help') }}
                </template>
            </InputLabel>
            <TextInput
                id="schedule-coaching-form-interactions-per-run-title"
                v-model="scheduleCoachingStore.formData.interactionsPerRun"
                :placeholder="__('schedule-coaching-form-interactions-per-run-title')"
                :width="TextInputWidth.Tiny"
                :type="'number'"
                min="1"
            />
        </div>

        <div class="border-1 border-gray-lightest rounded-sm p-5">
            <ScheduleCoachingIntervalForm
                v-for="(interval, index) in scheduleCoachingStore.formData.intervals"
                :key="index"
                :interval="interval"
                :index="index"
            />
        </div>
    </FormGroup>
</template>
